import type { CourseOutline, CourseProgress, RubricData, SourceData } from "../api/client";

/**
 * Datos del curso embebidos.
 *
 * El backend es la fuente de verdad (`GET /api/course/outline`), pero el
 * navegador tiene que poder dibujar la navegación aunque el backend esté
 * caído o el bundle todavía no esté indexado. Estos son esos datos de
 * respaldo: el sílabo destilado, no una invención.
 */

export const COURSE_TITLE = "Modelos Financieros y Evaluación de Proyectos";
export const COURSE_PROGRAM = "Maestría · UPC";

export const FALLBACK_OUTLINE: CourseOutline = {
  units: [
    {
      unit: "1",
      title: "Diagnóstico financiero",
      modules: [
        {
          module: "1",
          title: "Estados financieros",
          unit: "1",
          topics: [
            "Balance general y estado de resultados",
            "Estructura del activo, pasivo y patrimonio",
            "Estado de flujo de efectivo",
            "Criterios de reconocimiento y devengo",
          ],
        },
        {
          module: "2",
          title: "Análisis de estados financieros",
          unit: "1",
          topics: [
            "Ratios de liquidez, gestión, solvencia y rentabilidad",
            "ROE, ROA y descomposición DuPont",
            "EBITDA y margen operativo",
            "PER y múltiplos de mercado",
          ],
        },
      ],
    },
    {
      unit: "2",
      title: "Capital de trabajo y costo de capital",
      modules: [
        {
          module: "3",
          title: "Capital de trabajo neto y fondo de maniobra",
          unit: "2",
          topics: [
            "Capital de trabajo neto (CTN)",
            "Fondo de maniobra y su financiamiento",
            "Gestión de cuentas por cobrar e inventarios",
          ],
        },
        {
          module: "4",
          title: "NOF, ciclo de caja y WACC",
          unit: "2",
          topics: [
            "Necesidades operativas de fondos (NOF)",
            "Ciclo de conversión de efectivo",
            "Costo promedio ponderado de capital (WACC)",
            "Estructura de capital objetivo",
          ],
        },
      ],
    },
    {
      unit: "3",
      title: "Evaluación de proyectos",
      modules: [
        {
          module: "5",
          title: "Tasa de interés y valor del dinero en el tiempo",
          unit: "3",
          topics: [
            "Valor actual y valor futuro",
            "Tasas nominales, efectivas y equivalentes",
            "Anualidades y perpetuidades",
          ],
        },
        {
          module: "6",
          title: "VAN, TIR, Payback y factibilidad",
          unit: "3",
          topics: [
            "Valor actual neto (VAN)",
            "Tasa interna de retorno (TIR)",
            "Periodo de recuperación descontado",
            "Criterios de factibilidad económica",
          ],
        },
        {
          module: "7",
          title: "Flujo de caja libre y proyectos de reemplazo",
          unit: "3",
          topics: [
            "Construcción del flujo de caja libre",
            "Capital de trabajo en el flujo del proyecto",
            "Proyectos de reemplazo y costos hundidos",
          ],
        },
        {
          module: "8",
          title: "Riesgo, sensibilidad, escenarios y Montecarlo",
          unit: "3",
          topics: [
            "Análisis de sensibilidad y punto de equilibrio",
            "Escenarios pesimista, base y optimista",
            "Simulación de Montecarlo",
            "Riesgo del proyecto y tasa ajustada",
          ],
        },
      ],
    },
  ],
  evaluation: {
    total_label: "Nota final del curso",
    formula: "Nota final = 20 % CL + 45 % EA + 35 % TF",
    components: [
      {
        code: "CL",
        label: "Controles de lectura",
        weight: 0.2,
        description: "Evaluaciones cortas en Coursera al cerrar cada módulo.",
        modules: ["1", "2", "3", "4", "5", "6", "7", "8"],
      },
      {
        code: "EA",
        label: "Evaluaciones de avance",
        weight: 0.45,
        description: "Exámenes en Blackboard sobre los módulos 2, 4 y 6.",
        modules: ["2", "4", "6"],
      },
      {
        code: "TF",
        label: "Trabajo final",
        weight: 0.35,
        description: "Evaluación de un proyecto real con presentación grupal en el módulo 8.",
        modules: ["8"],
      },
    ],
  },
};

/** Cronograma de referencia: 8 semanas, un módulo por semana. */
export interface ScheduleWeek {
  label: string;
  title: string;
  description: string;
  duration: string;
  tags: string[];
  status: "done" | "current" | "upcoming";
}

export const COURSE_SCHEDULE: ScheduleWeek[] = [
  {
    label: "Semana 1",
    title: "Estados financieros",
    description: "Balance general, estado de resultados y flujo de efectivo. Control de lectura 1.",
    duration: "Módulo 1",
    tags: ["CL 1"],
    status: "done",
  },
  {
    label: "Semana 2",
    title: "Análisis de estados financieros",
    description: "Ratios, ROE/ROA, EBITDA y PER. Primera evaluación de avance.",
    duration: "Módulo 2",
    tags: ["EA 1"],
    status: "done",
  },
  {
    label: "Semana 3",
    title: "Capital de trabajo neto",
    description: "CTN, fondo de maniobra y gestión del circulante.",
    duration: "Módulo 3",
    tags: ["CL 3"],
    status: "done",
  },
  {
    label: "Semana 4",
    title: "NOF, ciclo de caja y WACC",
    description: "Ciclo de conversión de efectivo y costo promedio ponderado de capital.",
    duration: "Módulo 4",
    tags: ["EA 2"],
    status: "current",
  },
  {
    label: "Semana 5",
    title: "Valor del dinero en el tiempo",
    description: "VA, VF, tasas equivalentes y anualidades.",
    duration: "Módulo 5",
    tags: ["CL 5"],
    status: "upcoming",
  },
  {
    label: "Semana 6",
    title: "VAN, TIR y Payback",
    description: "Criterios de decisión y factibilidad económica. Tercera evaluación de avance.",
    duration: "Módulo 6",
    tags: ["EA 3"],
    status: "upcoming",
  },
  {
    label: "Semana 7",
    title: "Flujo de caja libre",
    description: "Construcción del FCL y proyectos de reemplazo.",
    duration: "Módulo 7",
    tags: ["CL 7"],
    status: "upcoming",
  },
  {
    label: "Semana 8",
    title: "Riesgo y Montecarlo",
    description: "Sensibilidad, escenarios y simulación. Presentación del trabajo final.",
    duration: "Módulo 8",
    tags: ["TF"],
    status: "upcoming",
  },
];

export const FALLBACK_PROGRESS: CourseProgress = {
  modules: [
    { module: "1", topics: 4, quizzes: 1, accuracy: 0.85 },
    { module: "2", topics: 4, quizzes: 2, accuracy: 0.7 },
    { module: "3", topics: 3, quizzes: 1, accuracy: 0.8 },
    { module: "4", topics: 4, quizzes: 1, accuracy: 0.6 },
    { module: "5", topics: 3, quizzes: 0, accuracy: 0 },
    { module: "6", topics: 4, quizzes: 0, accuracy: 0 },
    { module: "7", topics: 3, quizzes: 0, accuracy: 0 },
    { module: "8", topics: 4, quizzes: 0, accuracy: 0 },
  ],
};

export const FALLBACK_EVALUATION: RubricData = FALLBACK_OUTLINE.evaluation ?? { components: [] };

/** Fuentes del sílabo, para que el rail tenga algo que mostrar sin backend. */
export const FALLBACK_SOURCES: SourceData[] = [
  { path: "M1 Estados Financieros.pdf", title: "Módulo 1 · Estados Financieros", kind: "pdf", trust: "human-reviewed" },
  { path: "M2 Estados Financieros.pdf", title: "Módulo 2 · Análisis de estados financieros", kind: "pdf", trust: "human-reviewed" },
  { path: "M3 modelos financieros.pdf", title: "Módulo 3 · Capital de trabajo", kind: "pdf" },
  { path: "M4 modelos financieros.pdf", title: "Módulo 4 · NOF, ciclo de caja y WACC", kind: "pdf" },
  { path: "M5. Tasa de interes y valor del dinero en el tiempo.pdf", title: "Módulo 5 · Valor del dinero en el tiempo", kind: "pdf" },
  { path: "M6. Conceptos básicos sobre evaluacion de proyectos.pdf", title: "Módulo 6 · Evaluación de proyectos", kind: "pdf" },
  { path: "M7. Flujo de caja libre en el contexto de un proyecto.pdf", title: "Módulo 7 · Flujo de caja libre", kind: "pdf" },
  { path: "M8. Elementos de la evaluación de proyectos.pdf", title: "Módulo 8 · Riesgo y evaluación", kind: "pdf" },
  { path: "Sílabo_Modelos_Financieros_y_Evaluación_de_Proyectos.pdf", title: "Sílabo del curso", kind: "pdf", trust: "human-reviewed" },
];

export function moduleById(id: string): { module: string; title: string; unit: string } | null {
  for (const unit of FALLBACK_OUTLINE.units) {
    for (const module of unit.modules) {
      if (module.module === id) return { module: module.module, title: module.title, unit: unit.unit };
    }
  }
  return null;
}
